import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_luxury_boho_008',
  name: 'Luxury Boho Sand',
  occasionType: 'BABY_SHOWER',
  description: 'A modern luxury Boho baby shower invitation featuring warm beige and sand tones, pampas grass, palm leaves, thin gold borders, and a minimal teddy beside a crescent moon.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'boho', 'sand', 'teddy', 'moon'],
  colorScheme: { primary: '#78350f', accent: '#a16207', bg: '#fef3c7' },
};

export function LuxuryBohoSand({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Emma Watson';
  const hostedBy = baby.hostedBy || data.hostName || 'Sarah & David Watson';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'NOVEMBER 22, 2026';
  let dayOfWeek = 'SUNDAY';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '01:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'THE OASIS SANCTUARY';
  const venueAddress = baby.venueAddress || data.eventVenue || '789 PAMPAS LANE, SANTA BARBARA, CA';
  const rsvpName = baby.rsvpName || 'Jessica';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 019-2834';
  const message = baby.message || 'Join us for a calm and joyful celebration in anticipation of our little one.';
  const dressCode = baby.dressCode || 'Warm neutrals, ivory or sage';

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4 flex flex-col items-center justify-center font-sans select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap');
        .font-serif-boho { font-family: 'Playfair Display', Georgia, serif; }
        .font-sans-boho { font-family: 'Montserrat', sans-serif; }
        .font-cursive-boho { font-family: 'Pinyon Script', cursive; }
        .font-title-boho { font-family: 'Cinzel', serif; }
        
        .boho-bg-sand {
          background: linear-gradient(180deg, #fdfbf7 0%, #f7f3e9 40%, #efeae0 80%, #e6decb 100%);
          position: relative;
        }
        
        /* Shimmer Overlay */
        .gold-shimmer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(251,191,36,0.1) 30%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.3) 100%);
          mix-blend-mode: overlay;
          z-index: 2;
        }

        /* Fine Paper Grain Texture */
        .paper-grain-boho {
          position: absolute;
          inset: 0;
          pointer-events: none;
          mix-blend-mode: multiply;
          opacity: 0.16;
          z-index: 1;
        }

        .gold-foil-text {
          background: linear-gradient(135deg, #78350f 0%, #b45309 30%, #d97706 50%, #b45309 70%, #78350f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 1px 1px rgba(255,255,255,0.9));
        }

        .falling-petal {
          position: absolute;
          width: 6px;
          height: 6px;
          background-color: rgba(217, 119, 6, 0.15);
          border-radius: 50% 0 50% 50%;
          animation: floatPetal 8s infinite linear;
        }
        @keyframes floatPetal {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(400px) rotate(360deg); opacity: 0; }
        }
      `}</style>

      {/* Main Card Frame */}
      <div className="relative w-full max-w-[540px] aspect-[1080/1920] shadow-2xl rounded-2xl overflow-hidden boho-bg-sand flex flex-col p-10 border-[5px] border-white/85">
        {/* Shimmer & Paper Texture Layers */}
        <div className="gold-shimmer" />
        <svg className="paper-grain-boho" width="100%" height="100%">
          <filter id="linenNoise">
            <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="3" result="noise" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.05 0" />
            <feComposite operator="in" in2="SourceGraphic" />
          </filter>
          <rect width="100%" height="100%" filter="url(#linenNoise)" />
        </svg>

        {/* Scattered Sparkles / Bokeh */}
        <div className="falling-petal top-[15%] left-[20%]" style={{ animationDelay: '0s', width: '8px', height: '4px' }} />
        <div className="falling-petal top-[30%] right-[25%]" style={{ animationDelay: '2s', width: '6px', height: '3px' }} />
        <div className="falling-petal top-[60%] left-[10%]" style={{ animationDelay: '4s', width: '5px', height: '3px' }} />
        <div className="falling-petal top-[75%] right-[15%]" style={{ animationDelay: '6s', width: '7px', height: '4px' }} />

        {/* Thin Gold Geometric Border */}
        <div className="absolute inset-4 border border-dashed border-[#d97706]/20 rounded-2xl pointer-events-none z-10" />
        <div className="absolute inset-5 border border-[#d97706]/40 rounded-2xl pointer-events-none z-10">
          <div className="absolute inset-2 border border-[#d97706]/25 rounded-xl pointer-events-none" />
          <div className="absolute top-2 left-2 w-4 h-4 border-t border-l border-[#d97706]" />
          <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-[#d97706]" />
          <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-[#d97706]" />
          <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-[#d97706]" />
        </div>

        {/* Minimal Watercolor Teddy beside Crescent Moon & Stars */}
        <div className="absolute top-8 inset-x-0 h-40 pointer-events-none z-10 flex justify-center">
          <svg viewBox="0 0 160 120" className="w-36 h-full">
            <path d="M 95 30 A 25 25 0 1 0 95 80 A 20 20 0 1 1 95 30 Z" fill="#d97706" opacity="0.4" />
            <circle cx="70" cy="72" r="11" fill="#78350f" opacity="0.3" />
            <circle cx="70" cy="54" r="8" fill="#78350f" opacity="0.35" />
            <circle cx="63" cy="48" r="3" fill="#78350f" opacity="0.35" />
            <circle cx="77" cy="48" r="3" fill="#78350f" opacity="0.35" />
            <circle cx="58" cy="65" r="3.5" fill="#78350f" opacity="0.3" />
            <circle cx="82" cy="65" r="3.5" fill="#78350f" opacity="0.3" />
            <circle cx="61" cy="80" r="4.5" fill="#78350f" opacity="0.3" />
            <circle cx="79" cy="80" r="4.5" fill="#78350f" opacity="0.3" />
            <polygon points="105,38 107,42 111,42 108,45 109,49 105,47 101,49 102,45 99,42 103,42" fill="#d97706" opacity="0.8" />
            <polygon points="118,52 119,55 122,55 120,57 121,60 118,58 115,60 116,57 114,55 117,55" fill="#d97706" opacity="0.6" />
          </svg>
        </div>

        {/* Dried Pampas Grass & Palm Leaves Illustrations at Bottom-Right & Top-Left */}
        <div className="absolute top-[-5px] left-[-5px] w-28 h-28 pointer-events-none z-10 opacity-80">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <path d="M 20 20 Q 45 35, 60 50" stroke="#d97706" strokeWidth="1" fill="none" strokeDasharray="1 1" />
            <path d="M 20 20 Q 35 45, 50 60" stroke="#78350f" strokeWidth="0.75" fill="none" opacity="0.5" />
            <path d="M 15 15 C 30 30, 40 45, 45 50" stroke="#78350f" strokeWidth="1.5" fill="none" />
          </svg>
        </div>

        <div className="absolute bottom-[-15px] right-[-15px] w-40 h-40 pointer-events-none z-10 opacity-90">
          <svg viewBox="0 0 100 100" className="w-full h-full transform rotate-180">
            <path d="M 20 20 Q 55 55, 70 80 M 20 20 Q 60 45, 85 65" stroke="#78350f" strokeWidth="1" fill="none" opacity="0.4" />
            <path d="M 10 10 Q 50 35, 75 40" stroke="#d97706" strokeWidth="1.5" fill="none" strokeDasharray="1.5 1" />
            <circle cx="45" cy="25" r="4.5" fill="#fdfbf7" stroke="#78350f" strokeWidth="0.5" />
            <circle cx="25" cy="45" r="4.5" fill="#fdfbf7" stroke="#78350f" strokeWidth="0.5" />
          </svg>
        </div>

        {/* Typography — centered luxury stationery layout */}
        <div className="relative z-20 flex flex-col justify-between h-full pt-48 pb-12 px-4">
          
          {/* Header section */}
          <div className="text-center">
            <p className="font-title-boho text-[8.5px] tracking-[0.48em] text-[#78350f]/60 uppercase mb-8">
              JOIN US TO CELEBRATE A
            </p>

            <h2 className="font-title-boho text-[4rem] text-[#78350f] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-1 gold-foil-text">
              BABY
            </h2>
            <h2 className="font-title-boho text-[4rem] text-[#78350f] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-9 gold-foil-text">
              SHOWER
            </h2>

            <svg viewBox="0 0 220 12" className="w-44 h-3 mx-auto mb-8" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#78350f" strokeWidth="0.9" opacity="0.45"/>
              <circle cx="110" cy="6" r="2.8" fill="#78350f" opacity="0.55"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#78350f" strokeWidth="0.9" opacity="0.45"/>
            </svg>

            <span className="font-title-boho text-[8.5px] text-[#a16207]/80 tracking-[0.42em] block mb-8 uppercase">
              IN HONOR OF
            </span>

            <h1 className="font-cursive-boho text-[5.8rem] text-[#78350f] leading-none mb-9 gold-foil-text">
              {motherName}
            </h1>

            <svg viewBox="0 0 220 12" className="w-36 h-3 mx-auto mb-5" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#78350f" strokeWidth="0.8" opacity="0.40"/>
              <circle cx="110" cy="6" r="2.2" fill="#78350f" opacity="0.48"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#78350f" strokeWidth="0.8" opacity="0.40"/>
            </svg>
          </div>

          {/* Details Section */}
          <div className="flex flex-col items-center gap-5 text-center font-sans-boho flex-grow justify-center">
            
            <div className="w-full max-w-[360px] mx-auto">
              <div className="border-t border-b border-[#78350f]/30 py-4 flex items-center justify-center">
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-boho text-[7px] tracking-[0.32em] text-[#78350f]/60 uppercase block mb-1">DAY</span>
                  <span className="font-title-boho text-[9px] tracking-wider text-[#78350f] font-semibold block">{dayOfWeek}</span>
                  <span className="font-serif-boho text-[11.5px] text-stone-800 font-semibold block mt-0.5">{formattedDate}</span>
                </div>
                <div className="w-px h-10 bg-[#78350f]/20 mx-5 shrink-0" />
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-boho text-[7px] tracking-[0.32em] text-[#78350f]/60 uppercase block mb-1">TIME</span>
                  <span className="font-title-boho text-[9px] tracking-wider text-[#78350f] font-semibold block">AT</span>
                  <span className="font-serif-boho text-[11.5px] text-stone-800 font-semibold block mt-0.5">{eventTime}</span>
                </div>
              </div>
            </div>

            <div className="w-full max-w-[340px] mx-auto text-center">
              <span className="font-title-boho text-[7px] tracking-[0.35em] text-[#78350f]/60 uppercase block mb-1.5">VENUE</span>
              <span className="font-title-boho text-[10px] tracking-[0.18em] text-[#78350f] font-semibold block uppercase">{venueName}</span>
              <span className="font-sans-boho text-[9px] text-stone-600 block leading-relaxed mt-1">{venueAddress}</span>
            </div>

            <div
              role="button"
              aria-label="RSVP Now"
              onClick={() => setShowRsvpModal(true)}
              className="cursor-pointer group"
            >
              <div
                className="px-9 py-2.5 rounded-full border border-[#78350f]/30 bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] group-hover:from-[#fde68a] group-hover:to-[#fde68a] transition-all duration-200"
                style={{ boxShadow: '0 2px 14px rgba(120,53,15,0.06)' }}
              >
                <span className="font-title-boho text-[8.5px] tracking-[0.38em] text-[#78350f] font-semibold uppercase block text-center">RSVP</span>
                <span className="font-sans-boho text-[9.5px] text-stone-700 font-medium block text-center mt-0.5">{rsvpName} · {rsvpPhone}</span>
              </div>
            </div>

            {dressCode && (
              <div
                className="inline-block px-5 py-1.5 rounded-full border border-[#78350f]/30"
                style={{ background: 'rgba(254,243,199,0.60)' }}
              >
                <span className="font-title-boho text-[7.5px] tracking-[0.28em] text-[#78350f] uppercase font-semibold">
                  DRESS CODE &ensp;·&ensp; {dressCode}
                </span>
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div className="text-center pt-4">
            <span className="font-serif-boho text-[10px] text-stone-600 block leading-relaxed max-w-[300px] mx-auto mb-3">
              "{message}"
            </span>

            <svg viewBox="0 0 180 10" className="w-36 h-2 mx-auto mb-3" fill="none">
              <line x1="0" y1="5" x2="72" y2="5" stroke="#78350f" strokeWidth="0.7" opacity="0.4"/>
              <circle cx="90" cy="5" r="2" fill="#78350f" opacity="0.5"/>
              <line x1="108" y1="5" x2="180" y2="5" stroke="#78350f" strokeWidth="0.7" opacity="0.4"/>
            </svg>

            <span className="font-serif-boho text-[11px] text-[#78350f]/80 block tracking-wide">
              Hosted with love by
            </span>
            <span className="font-title-boho text-[9.5px] tracking-[0.28em] text-stone-800 font-semibold block mt-1 uppercase">
              {hostedBy}
            </span>
          </div>

        </div>

      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: LuxuryBohoSand });
